import "./styles.scss";

import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Parser from "@helpers/parser";
import {
  Text,
  Table,
  StatusChip,
  ChipPicker,
  Button,
  ButtonGroup,
  Popper,
  Input,
  Icon,
  MetricCard,
} from "@fragments";
import { UserAvatar, CommunityBanner } from "@components/Community";
import TabsSelector from "@components/TabsSelector";
import useConfigHeader from "@pages/CommunityConfig/hooks/useConfigHeader";
import {
  IconUsersGroup,
  IconFilterOff,
  IconDots,
  IconEye,
  IconCheck,
  IconX,
  IconBan,
  IconMailOff,
  IconSearch,
  IconUserPlus,
  IconArrowRight,
  IconMessageCheck,
  IconBrandWhatsapp,
  IconPercentage,
  IconCalendarStats,
  IconCoin,
  IconClipboardText,
} from "@tabler/icons-react";

import { getDurationUnitLabel } from "../../enums/durationUnits";
import {
  getEnrollmentStatuses,
  getEnrollmentStatusLabel,
} from "../../enums/enrollmentStatuses";
import { getEnrollmentSources } from "../../enums/enrollmentSources";
import {
  getEnrollmentSorts,
  getEnrollmentSortLabel,
} from "../../enums/enrollmentSorts";
import {
  getEnrollmentTabs,
  PENDING_TAB,
  DEFAULT_ENROLLMENT_TAB,
} from "../../enums/enrollmentTabs";

// Option lists are built from the caller's `t` so they follow the active language.
const getStatusOptions = (t) =>
  getEnrollmentStatuses(t).map((s) => ({ id: s.id, label: s.label }));
const getSourceOptions = (t) =>
  getEnrollmentSources(t).map((s) => ({ id: s.id, label: s.label }));
const getSortOptions = (t) =>
  getEnrollmentSorts(t).map((s) => ({ id: s.id, label: s.label }));

const SEARCH_DEBOUNCE_MS = 350;

// Quick approve/reject pills shown inline for pending rows — open the shared
// EnrollmentActionModal (§6.7) via onEnrollmentAction, like the row-menu items.
const getQuickActions = (t) => [
  { value: "approve", label: t("enrollments.rowActions.approve"), icon: <IconCheck size={15} /> },
  { value: "reject", label: t("enrollments.rowActions.reject"), icon: <IconX size={15} /> },
];

export default function MentoringEnrollmentsView({
  program,
  programLoading,
  enrollments,
  loading,
  page,
  pages,
  records,
  activeTab,
  filters,
  sort,
  search,
  hasFilters,
  pendingCount,
  pendingLoading,
  changeTab,
  setStatusFilter,
  setSourceFilter,
  changeSort,
  handleSearch,
  clearFilters,
  setPage,
  onInvite,
  onEnrollmentAction,
}) {
  const { t } = useTranslation("mentoringPrograms");
  const navigate = useNavigate();
  const { slug } = useParams();
  const popperRef = useRef();

  // Carry the active tab so the detail page's "Voltar" returns to it. The list
  // mirrors the tab in ?tab=; the default tab is canonical and needs no param.
  const tabQuery =
    activeTab && activeTab !== DEFAULT_ENROLLMENT_TAB ? `?tab=${activeTab}` : "";

  function openDetail(item) {
    navigate(`/${slug}/settings/mentoring/${item.program_id}/${item.id}${tabQuery}`);
  }

  // Jumps straight to the application's answers page, mirroring the detail
  // page's "Visualizar aplicação". form_id lives on the program; the submission
  // id is per-enrollment (form_submission_id). return_to/mentoring/phone drive
  // the answers-page review bar. NOTE: the list endpoint (#6) doesn't return
  // form_submission_id yet — the button is hidden until it does.
  function openApplication(event, item) {
    event.stopPropagation();
    const formId = program?.application?.form_id;
    const submissionId = item.form_submission_id;
    if (!formId || !submissionId) return;
    const returnTo = `/${slug}/settings/mentoring/${item.program_id}/${item.id}${tabQuery}`;
    const params = new URLSearchParams({
      form_id: formId,
      return_to: returnTo,
      mentoring: item.status,
    });
    if (item.creator?.phone) params.set("phone", item.creator.phone);
    navigate(`/${slug}/settings/forms/submissions/${submissionId}?${params.toString()}`);
  }

  function openRowMenu(event, item) {
    event.stopPropagation();
    const options = [];
    if (item.status === "pending") {
      options.push(
        {
          id: "approve",
          label: t("enrollments.rowActions.approve"),
          icon: <IconCheck size={15} />,
          onClick: async () => onEnrollmentAction?.(item, "accept"),
        },
        {
          id: "reject",
          label: t("enrollments.rowActions.reject"),
          icon: <IconX size={15} />,
          variant: "danger",
          onClick: async () => onEnrollmentAction?.(item, "reject"),
        },
      );
    }
    if (item.status === "active") {
      options.push({
        id: "end",
        label: t("enrollments.rowActions.end"),
        icon: <IconBan size={15} />,
        variant: "danger",
        onClick: async () => onEnrollmentAction?.(item, "end"),
      });
    }
    options.push({
      id: "view",
      label: t("enrollments.rowActions.view"),
      icon: <IconEye size={15} />,
      onClick: async () => openDetail(item),
    });
    popperRef.current.open(options, "bottom-end", event.currentTarget);
  }

  const allColumns = [
    {
      id: "creator",
      title: t("enrollments.columns.creator"),
      grow: 3,
      minWidth: 200,
      customRender: (item) =>
        item.creator ? (
          <div className="enr-creator">
            <UserAvatar
              user={{
                username: item.creator.name,
                avatar: item.creator.avatar_url,
              }}
              size={42}
            />
            <div className="enr-creator-text">
              <Text.h6 contrast={7} weight={500} maxLines={1}>
                {item.creator.name}
              </Text.h6>
              {item.creator.email && (
                <Text.span contrast={5} maxLines={1}>
                  {item.creator.email}
                </Text.span>
              )}
            </div>
          </div>
        ) : (
          <div className="enr-invited">
            <span className="enr-invited-icon">
              <IconMailOff size={15} />
            </span>
            <div className="enr-invited-text">
              <Text.span contrast={7} maxLines={1}>
                {item.invited_email}
              </Text.span>
              <StatusChip.Default text={t("enrollments.invitedChip")} />
            </div>
          </div>
        ),
    },
    {
      id: "status",
      title: t("common:fields.status"),
      minWidth: 150,
      align: "center",
      customRender: (item) => <EnrollmentStatusChip status={item.status} />,
    },
    {
      id: "community",
      title: t("enrollments.columns.community"),
      grow: 2,
      minWidth: 180,
      customRender: (item) =>
        item.community ? (
          <div className="enr-community">
            <CommunityBanner banner={item.community.banner_url} size={28} />
            <div className="enr-community-text">
              <Text.span contrast={7} maxLines={1}>
                {item.community.name}
              </Text.span>
              <Text.span contrast={5}>
                {t("enrollments.membersCount", { value: item.community.members_count })}
              </Text.span>
            </div>
          </div>
        ) : (
          <Text.span contrast={3}>—</Text.span>
        ),
    },
    {
      id: "revenue",
      title: t("enrollments.columns.revenue"),
      minWidth: 110,
      align: "right",
      render: (item) =>
        item.community?.revenue_cents != null
          ? `R$ ${Parser.price.brl(item.community.revenue_cents)}`
          : "—",
    },
    {
      id: "dates",
      title: t("enrollments.columns.dates"),
      minWidth: 170,
      customRender: (item) => <EnrollmentDates timestamps={item.timestamps} />,
    },
  ];

  // Dedicated "Abrir aplicação" column — only used in the pending tab, where the
  // table is a focused review view (creator + status + application + actions).
  const applicationColumn = {
    id: "application",
    title: t("enrollments.columns.application"),
    minWidth: 170,
    align: "center",
    customRender: (item) =>
      program?.application?.form_id && item.form_submission_id ? (
        <Button
          size="small"
          color="overlay"
          variant="rounded"
          startIcon={<IconClipboardText size={15} />}
          text={t("enrollments.openApplication")}
          tooltip={t("enrollments.openApplicationTooltip")}
          onClick={(e) => openApplication(e, item)}
          preventLoading
        />
      ) : (
        <Text.span contrast={3}>—</Text.span>
      ),
  };

  const columns =
    activeTab === PENDING_TAB
      ? [allColumns[0], allColumns[1], applicationColumn]
      : allColumns;

  const tabs = getEnrollmentTabs(t).map((tab) => ({
    key: tab.key,
    label: tab.label,
    countBadge:
      tab.key === PENDING_TAB && pendingCount > 0 ? pendingCount : undefined,
  }));

  // Program identity lives in the floating settings header: its name as the
  // title (a loading label until it resolves), its status as the subtitle.
  const programReady = !programLoading && !!program;
  useConfigHeader({
    title: programReady ? program.name : t("enrollments.loadingProgram"),
    subtitle: programReady
      ? program.active ? t("common:status.published") : t("common:status.draft")
      : undefined,
    icon: <IconUsersGroup />,
  });

  return (
    <div className="mentoring-enrollments px-4">
      <div className="enr-top">
        <div className="enr-metrics-row">
          <ProgramMetrics program={program} loading={programLoading || !program} />
          <PendingApprovalsCard
            count={pendingCount}
            loading={pendingLoading}
            onReview={() => changeTab(PENDING_TAB)}
            onInvite={onInvite}
          />
        </div>
      </div>

      <div className="enr-tabs-row">
        <TabsSelector tabs={tabs} value={activeTab} onChange={changeTab} />
        <EnrollmentSearch value={search} onSearch={handleSearch} />
      </div>

      {activeTab === "all" && (
        <div className="enr-controls">
          <div className="enr-filters">
            <Text.span contrast={5}>{t("enrollments.filters.label")}</Text.span>
            <ChipPicker
              title={t("common:fields.status")}
              multiple
              activateOnHover
              value={filters.status}
              options={getStatusOptions(t)}
              onChange={(next) => setStatusFilter(next)}
            />
            <ChipPicker
              title={t("enrollments.filters.source")}
              multiple
              activateOnHover
              value={filters.source}
              options={getSourceOptions(t)}
              onChange={(next) => setSourceFilter(next)}
            />
            <ChipPicker
              title={t("enrollments.filters.sort")}
              activateOnHover
              value={sort}
              valueLabel={getEnrollmentSortLabel(t)[sort]}
              options={getSortOptions(t)}
              onChange={(id) => changeSort(id)}
            />
            {hasFilters && (
              <Button
                color="overlay"
                size="small"
                startIcon={<IconFilterOff size={16} />}
                text={t("enrollments.filters.clear")}
                onClick={clearFilters}
                preventLoading
              />
            )}
          </div>
        </div>
      )}

      <Table
        id="mentoring-enrollments-table"
        columns={columns}
        data={enrollments}
        loading={loading}
        loadingRows={6}
        contained
        enablePagination={pages > 1}
        page={page}
        pages={pages}
        records={records}
        onSelectPage={setPage}
        onRowClick={openDetail}
        emptyMessage={t("enrollments.emptyMessage")}
        emptySubtitle={t("enrollments.emptySubtitle")}
        emptyIcon={<IconUsersGroup size={36} />}
        actionsComponent={(item) => (
          <div className="enr-row-actions">
            {activeTab === PENDING_TAB && item.status === "pending" && (
              <div
                className="enr-quick-actions"
                onClick={(e) => e.stopPropagation()}
              >
                <ButtonGroup
                  size="small"
                  options={getQuickActions(t)}
                  onChange={(value) =>
                    onEnrollmentAction?.(
                      item,
                      value === "approve" ? "accept" : "reject",
                    )
                  }
                />
              </div>
            )}
            {item.creator?.phone && (
              <Button
                size="small"
                color="text"
                tooltip={t("enrollments.whatsappTooltip")}
                icon={<IconBrandWhatsapp size={16} />}
                onClick={(e) => openWhatsApp(e, item.creator.phone)}
                preventLoading
              />
            )}
            <Button
              size="small"
              color="text"
              icon={<IconDots size={16} />}
              onMouseEnter={(e) => openRowMenu(e, item)}
              onClick={(e) => openRowMenu(e, item)}
            />
          </div>
        )}
      />

      <Popper controlRef={popperRef} config={{ activateOnHover: true }} />
    </div>
  );
}

// Debounced search by creator name / email — mirrors the Forms submissions
// search UX. Sends the committed term to the hook after the user stops typing.
function EnrollmentSearch({ value, onSearch }) {
  const { t } = useTranslation("mentoringPrograms");
  const [input, setInput] = useState(value ?? "");

  useEffect(() => {
    const timer = setTimeout(() => {
      if (input.trim() !== (value ?? "").trim()) onSearch(input);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [input, value, onSearch]);

  return (
    <div className="enr-search">
      <Input.Text
        value={input}
        onInput={setInput}
        onBlur={() => input.trim() !== (value ?? "").trim() && onSearch(input)}
        placeholder={t("enrollments.searchPlaceholder")}
        start={<IconSearch size={16} />}
        fullWidth
      />
    </div>
  );
}

// Pending-approvals card — sits beside the program title (§6.3). Three states:
// loading · "Você tem X aplicações aguardando aprovação" · "Não há nenhuma
// aplicação pendente, convide um mentorado". Uses people-circle.png for now.
function PendingApprovalsCard({ count, loading, onReview, onInvite }) {
  const { t } = useTranslation("mentoringPrograms");
  const hasPending = count > 0;

  return (
    <div className={`enr-pending-card${loading ? " is-loading" : ""}`}>
      <Icon.Sphere children={<IconMessageCheck />} size={85}/>
      <div className="epc-text">
        {loading ? (
          <>
            <Text.h5 contrast={9} weight={500} loading>
              {t("enrollments.pendingCard.loadingTitle")}
            </Text.h5>
            <Text.span contrast={6} loading>
              {t("enrollments.pendingCard.loadingSubtitle")}
            </Text.span>
          </>
        ) : hasPending ? (
          <>
            <Text.h5 contrast={9} weight={500}>
              {t("enrollments.pendingCard.title", { count })}
            </Text.h5>
            <Text.span contrast={6}>
              {t("enrollments.pendingCard.description")}
            </Text.span>
            <Button
              text={t("enrollments.pendingCard.review")}
              endIcon={<IconArrowRight />}
              color="inverse"
              highlightColor="current"
              variant="rounded"
              size="small"
              className="epc-cta"
              onClick={onReview}
            />
          </>
        ) : (
          <>
            <Text.h5 contrast={9} weight={500}>
              {t("enrollments.pendingCard.emptyTitle")}
            </Text.h5>
            <Text.span contrast={6}>
              {t("enrollments.pendingCard.emptyDescription")}
            </Text.span>
            <Button
              text={t("enrollments.pendingCard.invite")}
              endIcon={<IconUserPlus />}
              color="inverse"
              highlightColor="current"
              variant="rounded"
              size="small"
              className="epc-cta"
              onClick={onInvite}
            />
          </>
        )}
      </div>
    </div>
  );
}

// Program terms + enrollment count rendered as a MetricCard strip (replaces the
// old text subtitle). Durations show their unit in the card's note line.
function ProgramMetrics({ program, loading }) {
  const { t } = useTranslation("mentoringPrograms");
  const p = program ?? {};
  const mentoring = durationCard(
    t,
    p.duration_value,
    p.duration_unit,
    t("enrollments.metrics.durationFallback"),
  );


  return (
    <div className="enr-program-metrics">
      <MetricCard
        title={t("enrollments.metrics.revenue")}
        leftIcon={<IconCoin size={15} />}
        value={
          p.metrics?.total_revenue_cents != null
            ? p.metrics.total_revenue_cents / 100
            : undefined
        }
        variant="money"
        loading={loading}
        note={t("enrollments.metrics.revenueNote")}
      />
      <MetricCard
        title={t("enrollments.metrics.split")}
        leftIcon={<IconPercentage size={15} />}
        value={p.split_percentage != null ? `${p.split_percentage}%` : "—"}
        loading={loading}
        note={t("enrollments.metrics.splitNote")}
      />
      <MetricCard
        title={t("enrollments.metrics.duration")}
        leftIcon={<IconCalendarStats size={15} />}
        value={mentoring.value}
        variant={mentoring.variant}
        note={mentoring.note}
        loading={loading}
      />
    </div>
  );
}

// Maps a duration value+unit to MetricCard props. When the value is absent the
// card shows an em dash and the fallback note (e.g. "indefinida").
function durationCard(t, value, unit, fallbackNote) {
  if (value) {
    return { value, variant: "number", note: getDurationUnitLabel(t)[unit] ?? null };
  }
  return { value: "—", variant: null, note: fallbackNote };
}

// Only active enrollments get the success chip; every other status stays on the
// neutral default variant (no alert/info/error tones for now).
export function EnrollmentStatusChip({ status }) {
  const { t } = useTranslation("mentoringPrograms");
  const Variant = status === "active" ? StatusChip.Success : StatusChip.Default;
  return <Variant text={getEnrollmentStatusLabel(t)[status] ?? status} />;
}

function EnrollmentDates({ timestamps = {} }) {
  const { t } = useTranslation("mentoringPrograms");
  const started = fmtDate(timestamps.started_at);
  if (!started) return <Text.span contrast={3}>—</Text.span>;

  const ends = fmtDate(timestamps.ends_at);
  const splitEnds = fmtDate(timestamps.split_ends_at);
  const showSplit = splitEnds && splitEnds !== ends;

  return (
    <div className="enr-dates">
      <Text.span contrast={7}>
        {started}
        {ends ? ` → ${ends}` : ""}
      </Text.span>
      {showSplit && (
        <Text.span contrast={5}>
          {t("enrollments.splitUntil", { date: splitEnds })}
        </Text.span>
      )}
    </div>
  );
}

// Opens the creator's WhatsApp in a new tab. wa.me expects digits only, so we
// strip the leading "+" and any spacing from the stored phone number.
function openWhatsApp(event, phone) {
  event.stopPropagation();
  const digits = String(phone).replace(/\D/g, "");
  if (digits) window.open(`https://wa.me/${digits}`, "_blank", "noopener");
}

function fmtDate(iso) {
  return iso ? Parser.date.ddmmyyyy(iso.slice(0, 10)) : null;
}
